import {
  CA_PROVINCES,
  CheckoutContext,
  US_STATES,
  isValidCAPostalFormat,
  isValidCAProvince,
  isValidPostalForProvince,
  isValidUSState,
  isValidZipForState,
} from "@/components/CheckoutForm/utils";
import { CheckoutSelect } from "@/components/CheckoutSelect/CheckoutSelect";
import { CheckoutTextInput } from "@/components/CheckoutTextInput/CheckoutTextInput";
import { UserApi } from "@/api/user.api";
import { CartService } from "@/stores/cart";
import { usePending } from "@/utils/hooks/usePending.js";
import { Button, Tooltip } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { useEffect, useState } from "react";

// Phase B follow-on (2026-07-24): billing country is WORLDWIDE (Michael's
// ruling 7/23 — a real country lets Stripe AVS verify and properly decline).
// Ship-to stays US + CA; the shipping step enforces that separately. ISO
// 3166-1 alpha-2 codes (what Stripe expects); US and Canada pinned on top.
export const BILLING_COUNTRIES = [
  { value: "US", label: "United States" },
  { value: "CA", label: "Canada" },
  { value: "AF", label: "Afghanistan" },
  { value: "AL", label: "Albania" },
  { value: "DZ", label: "Algeria" },
  { value: "AS", label: "American Samoa" },
  { value: "AD", label: "Andorra" },
  { value: "AO", label: "Angola" },
  { value: "AI", label: "Anguilla" },
  { value: "AQ", label: "Antarctica" },
  { value: "AG", label: "Antigua and Barbuda" },
  { value: "AR", label: "Argentina" },
  { value: "AM", label: "Armenia" },
  { value: "AW", label: "Aruba" },
  { value: "AU", label: "Australia" },
  { value: "AT", label: "Austria" },
  { value: "AZ", label: "Azerbaijan" },
  { value: "BS", label: "Bahamas" },
  { value: "BH", label: "Bahrain" },
  { value: "BD", label: "Bangladesh" },
  { value: "BB", label: "Barbados" },
  { value: "BY", label: "Belarus" },
  { value: "BE", label: "Belgium" },
  { value: "BZ", label: "Belize" },
  { value: "BJ", label: "Benin" },
  { value: "BM", label: "Bermuda" },
  { value: "BT", label: "Bhutan" },
  { value: "BO", label: "Bolivia" },
  { value: "BQ", label: "Bonaire, Sint Eustatius and Saba" },
  { value: "BA", label: "Bosnia and Herzegovina" },
  { value: "BW", label: "Botswana" },
  { value: "BV", label: "Bouvet Island" },
  { value: "BR", label: "Brazil" },
  { value: "IO", label: "British Indian Ocean Territory" },
  { value: "BN", label: "Brunei Darussalam" },
  { value: "BG", label: "Bulgaria" },
  { value: "BF", label: "Burkina Faso" },
  { value: "BI", label: "Burundi" },
  { value: "CV", label: "Cabo Verde" },
  { value: "KH", label: "Cambodia" },
  { value: "CM", label: "Cameroon" },
  { value: "KY", label: "Cayman Islands" },
  { value: "CF", label: "Central African Republic" },
  { value: "TD", label: "Chad" },
  { value: "CL", label: "Chile" },
  { value: "CN", label: "China" },
  { value: "CX", label: "Christmas Island" },
  { value: "CC", label: "Cocos (Keeling) Islands" },
  { value: "CO", label: "Colombia" },
  { value: "KM", label: "Comoros" },
  { value: "CG", label: "Congo" },
  { value: "CD", label: "Congo, The Democratic Republic of the" },
  { value: "CK", label: "Cook Islands" },
  { value: "CR", label: "Costa Rica" },
  { value: "HR", label: "Croatia" },
  { value: "CU", label: "Cuba" },
  { value: "CW", label: "Curaçao" },
  { value: "CY", label: "Cyprus" },
  { value: "CZ", label: "Czechia" },
  { value: "CI", label: "Côte d'Ivoire" },
  { value: "DK", label: "Denmark" },
  { value: "DJ", label: "Djibouti" },
  { value: "DM", label: "Dominica" },
  { value: "DO", label: "Dominican Republic" },
  { value: "EC", label: "Ecuador" },
  { value: "EG", label: "Egypt" },
  { value: "SV", label: "El Salvador" },
  { value: "GQ", label: "Equatorial Guinea" },
  { value: "ER", label: "Eritrea" },
  { value: "EE", label: "Estonia" },
  { value: "SZ", label: "Eswatini" },
  { value: "ET", label: "Ethiopia" },
  { value: "FK", label: "Falkland Islands (Malvinas)" },
  { value: "FO", label: "Faroe Islands" },
  { value: "FJ", label: "Fiji" },
  { value: "FI", label: "Finland" },
  { value: "FR", label: "France" },
  { value: "GF", label: "French Guiana" },
  { value: "PF", label: "French Polynesia" },
  { value: "TF", label: "French Southern Territories" },
  { value: "GA", label: "Gabon" },
  { value: "GM", label: "Gambia" },
  { value: "GE", label: "Georgia" },
  { value: "DE", label: "Germany" },
  { value: "GH", label: "Ghana" },
  { value: "GI", label: "Gibraltar" },
  { value: "GR", label: "Greece" },
  { value: "GL", label: "Greenland" },
  { value: "GD", label: "Grenada" },
  { value: "GP", label: "Guadeloupe" },
  { value: "GU", label: "Guam" },
  { value: "GT", label: "Guatemala" },
  { value: "GG", label: "Guernsey" },
  { value: "GN", label: "Guinea" },
  { value: "GW", label: "Guinea-Bissau" },
  { value: "GY", label: "Guyana" },
  { value: "HT", label: "Haiti" },
  { value: "HM", label: "Heard Island and McDonald Islands" },
  { value: "VA", label: "Holy See (Vatican City State)" },
  { value: "HN", label: "Honduras" },
  { value: "HK", label: "Hong Kong" },
  { value: "HU", label: "Hungary" },
  { value: "IS", label: "Iceland" },
  { value: "IN", label: "India" },
  { value: "ID", label: "Indonesia" },
  { value: "IR", label: "Iran" },
  { value: "IQ", label: "Iraq" },
  { value: "IE", label: "Ireland" },
  { value: "IM", label: "Isle of Man" },
  { value: "IL", label: "Israel" },
  { value: "IT", label: "Italy" },
  { value: "JM", label: "Jamaica" },
  { value: "JP", label: "Japan" },
  { value: "JE", label: "Jersey" },
  { value: "JO", label: "Jordan" },
  { value: "KZ", label: "Kazakhstan" },
  { value: "KE", label: "Kenya" },
  { value: "KI", label: "Kiribati" },
  { value: "KW", label: "Kuwait" },
  { value: "KG", label: "Kyrgyzstan" },
  { value: "LA", label: "Laos" },
  { value: "LV", label: "Latvia" },
  { value: "LB", label: "Lebanon" },
  { value: "LS", label: "Lesotho" },
  { value: "LR", label: "Liberia" },
  { value: "LY", label: "Libya" },
  { value: "LI", label: "Liechtenstein" },
  { value: "LT", label: "Lithuania" },
  { value: "LU", label: "Luxembourg" },
  { value: "MO", label: "Macao" },
  { value: "MG", label: "Madagascar" },
  { value: "MW", label: "Malawi" },
  { value: "MY", label: "Malaysia" },
  { value: "MV", label: "Maldives" },
  { value: "ML", label: "Mali" },
  { value: "MT", label: "Malta" },
  { value: "MH", label: "Marshall Islands" },
  { value: "MQ", label: "Martinique" },
  { value: "MR", label: "Mauritania" },
  { value: "MU", label: "Mauritius" },
  { value: "YT", label: "Mayotte" },
  { value: "MX", label: "Mexico" },
  { value: "FM", label: "Micronesia, Federated States of" },
  { value: "MD", label: "Moldova" },
  { value: "MC", label: "Monaco" },
  { value: "MN", label: "Mongolia" },
  { value: "ME", label: "Montenegro" },
  { value: "MS", label: "Montserrat" },
  { value: "MA", label: "Morocco" },
  { value: "MZ", label: "Mozambique" },
  { value: "MM", label: "Myanmar" },
  { value: "NA", label: "Namibia" },
  { value: "NR", label: "Nauru" },
  { value: "NP", label: "Nepal" },
  { value: "NL", label: "Netherlands" },
  { value: "NC", label: "New Caledonia" },
  { value: "NZ", label: "New Zealand" },
  { value: "NI", label: "Nicaragua" },
  { value: "NE", label: "Niger" },
  { value: "NG", label: "Nigeria" },
  { value: "NU", label: "Niue" },
  { value: "NF", label: "Norfolk Island" },
  { value: "KP", label: "North Korea" },
  { value: "MK", label: "North Macedonia" },
  { value: "MP", label: "Northern Mariana Islands" },
  { value: "NO", label: "Norway" },
  { value: "OM", label: "Oman" },
  { value: "PK", label: "Pakistan" },
  { value: "PW", label: "Palau" },
  { value: "PS", label: "Palestine, State of" },
  { value: "PA", label: "Panama" },
  { value: "PG", label: "Papua New Guinea" },
  { value: "PY", label: "Paraguay" },
  { value: "PE", label: "Peru" },
  { value: "PH", label: "Philippines" },
  { value: "PN", label: "Pitcairn" },
  { value: "PL", label: "Poland" },
  { value: "PT", label: "Portugal" },
  { value: "PR", label: "Puerto Rico" },
  { value: "QA", label: "Qatar" },
  { value: "RO", label: "Romania" },
  { value: "RU", label: "Russian Federation" },
  { value: "RW", label: "Rwanda" },
  { value: "RE", label: "Réunion" },
  { value: "BL", label: "Saint Barthélemy" },
  { value: "SH", label: "Saint Helena, Ascension and Tristan da Cunha" },
  { value: "KN", label: "Saint Kitts and Nevis" },
  { value: "LC", label: "Saint Lucia" },
  { value: "MF", label: "Saint Martin (French part)" },
  { value: "PM", label: "Saint Pierre and Miquelon" },
  { value: "VC", label: "Saint Vincent and the Grenadines" },
  { value: "WS", label: "Samoa" },
  { value: "SM", label: "San Marino" },
  { value: "ST", label: "Sao Tome and Principe" },
  { value: "SA", label: "Saudi Arabia" },
  { value: "SN", label: "Senegal" },
  { value: "RS", label: "Serbia" },
  { value: "SC", label: "Seychelles" },
  { value: "SL", label: "Sierra Leone" },
  { value: "SG", label: "Singapore" },
  { value: "SX", label: "Sint Maarten (Dutch part)" },
  { value: "SK", label: "Slovakia" },
  { value: "SI", label: "Slovenia" },
  { value: "SB", label: "Solomon Islands" },
  { value: "SO", label: "Somalia" },
  { value: "ZA", label: "South Africa" },
  { value: "GS", label: "South Georgia and the South Sandwich Islands" },
  { value: "KR", label: "South Korea" },
  { value: "SS", label: "South Sudan" },
  { value: "ES", label: "Spain" },
  { value: "LK", label: "Sri Lanka" },
  { value: "SD", label: "Sudan" },
  { value: "SR", label: "Suriname" },
  { value: "SJ", label: "Svalbard and Jan Mayen" },
  { value: "SE", label: "Sweden" },
  { value: "CH", label: "Switzerland" },
  { value: "SY", label: "Syria" },
  { value: "TW", label: "Taiwan" },
  { value: "TJ", label: "Tajikistan" },
  { value: "TZ", label: "Tanzania" },
  { value: "TH", label: "Thailand" },
  { value: "TL", label: "Timor-Leste" },
  { value: "TG", label: "Togo" },
  { value: "TK", label: "Tokelau" },
  { value: "TO", label: "Tonga" },
  { value: "TT", label: "Trinidad and Tobago" },
  { value: "TN", label: "Tunisia" },
  { value: "TM", label: "Turkmenistan" },
  { value: "TC", label: "Turks and Caicos Islands" },
  { value: "TV", label: "Tuvalu" },
  { value: "TR", label: "Türkiye" },
  { value: "UG", label: "Uganda" },
  { value: "UA", label: "Ukraine" },
  { value: "AE", label: "United Arab Emirates" },
  { value: "GB", label: "United Kingdom" },
  { value: "UM", label: "United States Minor Outlying Islands" },
  { value: "UY", label: "Uruguay" },
  { value: "UZ", label: "Uzbekistan" },
  { value: "VU", label: "Vanuatu" },
  { value: "VE", label: "Venezuela" },
  { value: "VN", label: "Vietnam" },
  { value: "VG", label: "Virgin Islands, British" },
  { value: "VI", label: "Virgin Islands, U.S." },
  { value: "WF", label: "Wallis and Futuna" },
  { value: "EH", label: "Western Sahara" },
  { value: "YE", label: "Yemen" },
  { value: "ZM", label: "Zambia" },
  { value: "ZW", label: "Zimbabwe" },
  { value: "AX", label: "Åland Islands" },
];

interface CheckoutBillingFormProps {
  ctx: CheckoutContext;
  nextIsPending: boolean;
  onNext: () => void;
  onPrev?: () => void;
}

export const CheckoutBillingForm = ({
  ctx,
  nextIsPending,
  onNext,
}: CheckoutBillingFormProps) => {
  const { data, setData } = ctx;
  const [showValidation, setShowValidation] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [validationError, setValidationError] = useState("");
  const isMobile = useMediaQuery("(max-width: 767px)");

  // GA (basil): updateEmail/updatePhoneNumber THROW on a session whose
  // attached customer already carries that field — true for every logged-in
  // user since the saved-cards customer attach. The customer record owns
  // those values; treat the Stripe-side update as best-effort and never let
  // a rejection wedge the Save & Continue spinner (found on staging
  // 2026-08-25: unhandled IntegrationError hung Billing step 1). Guests
  // (no customer on the session) still update normally.
  const updatePhoneNumber = usePending(async (phone: string) => {
    try {
      await ctx.checkout.updatePhoneNumber(phone);
    } catch (error) {
      console.warn("[checkout] updatePhoneNumber skipped:", error);
    }
  });
  const updateEmail = usePending(async (email: string) => {
    try {
      await ctx.checkout.updateEmail(email);
    } catch (error) {
      console.warn("[checkout] updateEmail skipped:", error);
    }
  });

  // Phase B follow-on (2026-07-24): which postal system the billing address
  // uses. US/CA validate strictly (existing helpers); any other country gets
  // free-form region + postal (Stripe AVS does the fine-grained work).
  const billingCountry = String(data.billingCountry || "US")
    .trim()
    .toUpperCase();
  const billingIsUS = billingCountry === "US";
  const billingIsCA = billingCountry === "CA";

  const updateShippingAddressForTax = usePending(async function () {
    if (
      data.shippingType === "shipToMyAddress" &&
      // Ship-to is US + CA only — a third-country billing address must not
      // be pushed as the Stripe SHIPPING address (the shipping step forces
      // ship-to-other for those customers).
      ["US", "CA"].includes(billingCountry) &&
      data.billingStreet?.trim() &&
      data.billingCity?.trim() &&
      data.billingState?.trim() &&
      data.billingZipcode?.trim()
    ) {
      await ctx.checkout.updateShippingAddress({
        name: `${data.firstName} ${data.lastName}`,
        address: {
          line1: data.billingStreet.trim(),
          line2: data.billingApartment || "",
          city: data.billingCity.trim(),
          state: data.billingState,
          postal_code: data.billingZipcode,
          country: data.billingCountry || "US",
        },
      });
    }
  });

  const updateBillingAddress = usePending(async function () {
    // FIX 2026-08-04 (AU customer blocked at checkout): for non-US/CA
    // billing countries the form labels State/Region "(Optional)" — and
    // switching country CLEARS it — yet this guard still demanded it. A
    // blank region meant the billing address was silently never pushed to
    // Stripe, so confirm() failed with "A complete billing address is
    // required to confirm this Checkout Session." State completes an
    // address only where the form itself requires it (US/CA); everywhere
    // else Stripe/AVS does the fine-grained work.
    const stateOk =
      billingIsUS || billingIsCA ? Boolean(data.billingState?.trim()) : true;
    if (
      data.billingStreet?.trim() &&
      data.billingCity?.trim() &&
      stateOk &&
      data.billingZipcode?.trim()
    ) {
      await ctx.checkout.updateBillingAddress({
        name: `${data.firstName} ${data.lastName}`,
        address: {
          line1: data.billingStreet.trim(),
          line2: data.billingApartment || "",
          city: data.billingCity.trim(),
          state: data.billingState,
          postal_code: data.billingZipcode,
          country: data.billingCountry || "US",
        },
      });
    }
  });

  useEffect(() => {
    if (showTooltip) {
      const timer = setTimeout(() => {
        setShowTooltip(false);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [showTooltip]);

  return (
    <form
      className="flex h-full flex-col gap-6 pt-1"
      noValidate
      onSubmit={async e => {
        e.preventDefault();
        // Phase B follow-on (2026-07-24): country-aware validation. US keeps
        // the original checks; CA mirrors the shipping form's province/postal
        // coherence; other countries require a postal code but validate
        // nothing further (regions are free-form worldwide — AVS decides).
        const isValidState = billingIsUS
          ? isValidUSState(data.billingState)
          : billingIsCA
            ? isValidCAProvince(data.billingState)
            : true;
        const isValidZip = billingIsUS
          ? isValidZipForState(data.billingZipcode || "", data.billingState)
          : billingIsCA
            ? isValidCAPostalFormat(data.billingZipcode || "") &&
              isValidPostalForProvince(
                data.billingZipcode || "",
                data.billingState
              )
            : Boolean((data.billingZipcode || "").trim());
        const stateRequired = billingIsUS || billingIsCA;
        if (
          !data.firstName ||
          !data.lastName ||
          !data.email ||
          !data.phoneNumber ||
          !data.billingStreet ||
          !data.billingCity ||
          !data.billingZipcode ||
          (stateRequired && !data.billingState) ||
          !isValidState ||
          !isValidZip
        ) {
          setShowValidation(true);
          if (!isValidState) {
            setValidationError(
              billingIsCA
                ? "Please select a valid province."
                : "Please select a valid state."
            );
          }
          if (!isValidZip) {
            setValidationError(
              billingIsUS
                ? "Please enter a valid ZIP code for the selected state."
                : billingIsCA
                  ? "Please check the province and postal code — they don't match."
                  : "Please enter a postal code."
            );
          } else {
            setValidationError("Please enter all required fields.");
          }
          setShowTooltip(true);
          return;
        }

        // Update shipping address for tax calculation before proceeding
        await updateShippingAddressForTax();
        await updateBillingAddress();

        // Save-back (2026-08-26, Michael-approved): a completed Billing step
        // seeds the account's billingDetails, so every later checkout (and
        // the profile add-card form) prefills. Canonical Stripe shape — the
        // same one Profile → User Details and the legacy seed write.
        // Best-effort: a failure must never block checkout.
        // NEVER for an admin session (2026-09-03): staff check out
        // staff-built / counter carts while signed in as the shop account,
        // and this save-back was overwriting info@'s profile with each
        // customer's billing details (Kristian Ruegg, Brian Pomeroy…).
        if (ctx.userEmail && !ctx.userIsAdmin) {
          try {
            await UserApi.updateUser({
              billingDetails: {
                firstName: data.firstName,
                lastName: data.lastName,
                phone: data.phoneNumber || "",
                address: {
                  line1: data.billingStreet.trim(),
                  line2: data.billingApartment || "",
                  city: data.billingCity.trim(),
                  state: data.billingState || "",
                  postal_code: data.billingZipcode,
                  country: data.billingCountry || "US",
                },
              },
            });
          } catch (error) {
            console.warn(
              "[checkout] billingDetails save-back skipped:",
              error
            );
          }
        }

        if (!ctx.userEmail && !ctx.cart?.name) {
          await CartService.update({
            name: `${data.firstName} ${data.lastName} — ${data.email}`,
          });
        }

        onNext();
      }}
    >
      <div className="flex h-full flex-col gap-6 px-4 pt-4 md:pt-0">
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <p className="mb-1 text-[18px] font-bold leading-none">
              Billing Contact
              <span className="block text-sm font-normal text-[#636363] md:ml-[10px] md:inline">
                (For order confirmation)
              </span>
            </p>
            {ctx.userBillingAddress && (
              <Button
                variant="subtle"
                size="xs"
                onClick={() => {
                  const billing = ctx.userBillingAddress as any;

                  if (!billing) return;

                  setData({
                    ...data,
                    firstName: billing?.firstName || data.firstName,
                    lastName: billing?.lastName || data.lastName,
                    email: ctx.userEmail || data.email,
                    billingStreet:
                      billing?.address?.line1 || data.billingStreet,
                    billingApartment:
                      billing?.address?.line2 || data.billingApartment,
                    billingCity: billing?.address?.city || data.billingCity,
                    billingState: billing?.address?.state || data.billingState,
                    billingZipcode:
                      billing?.address?.postal_code || data.billingZipcode,
                    billingCountry:
                      billing?.address?.country || data.billingCountry,
                    phoneNumber: billing?.phone || data.phoneNumber,
                  });
                }}
              >
                Use Saved Info
              </Button>
            )}
          </div>
          <div className="flex flex-wrap gap-3 md:flex-nowrap">
            <CheckoutTextInput
              label="First Name"
              name="firstName"
              value={data.firstName || ""}
              onChange={value => setData({ ...data, firstName: value })}
              required
              showValidation={showValidation}
            />
            <CheckoutTextInput
              label="Last Name"
              name="lastName"
              value={data.lastName || ""}
              onChange={value => setData({ ...data, lastName: value })}
              required
              showValidation={showValidation}
            />
            <CheckoutTextInput
              label="Company (Optional)"
              name="company"
              value={data.companyName || ""}
              onChange={value => setData({ ...data, companyName: value })}
              flex={!isMobile}
              className="md:w-unset w-full"
            />
          </div>
          <div className="flex gap-3">
            <CheckoutTextInput
              label="Email"
              name="email"
              type="email"
              value={data.email}
              onChange={value => setData({ ...data, email: value })}
              onBlur={() => updateEmail(data.email)}
              required
              showValidation={showValidation}
            />
            <CheckoutTextInput
              label="Phone Number"
              name="phoneNumber"
              type="tel"
              value={data.phoneNumber}
              onChange={value => setData({ ...data, phoneNumber: value })}
              onBlur={() => updatePhoneNumber(data.phoneNumber)}
              required
              showValidation={showValidation}
            />
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <p className="mb-1 text-[18px] font-bold leading-none">
            Billing Address
          </p>
          <div className="flex gap-3">
            <CheckoutTextInput
              label="Street"
              name="billingStreet"
              value={data.billingStreet || ""}
              onChange={value => setData({ ...data, billingStreet: value })}
              required
              showValidation={showValidation}
            />
            <CheckoutTextInput
              label="Apartment"
              name="billingApartment"
              value={data.billingApartment || ""}
              onChange={value => setData({ ...data, billingApartment: value })}
            />
          </div>
          {/* Phase B follow-on (2026-07-24): worldwide billing country —
              switching clears state+code so a stale pair can't contradict
              the new country's postal system (same rule as the shipping
              form's picker). */}
          <div className="flex gap-3">
            <CheckoutSelect
              label="Country"
              name="billingCountry"
              value={billingCountry}
              onChange={value => {
                if (value !== billingCountry) {
                  setData({
                    ...data,
                    billingCountry: value,
                    billingState: "",
                    billingZipcode: "",
                  });
                }
              }}
              data={BILLING_COUNTRIES}
              required
              showValidation={showValidation}
            />
          </div>
          <div className="flex flex-wrap gap-3 md:flex-nowrap">
            <CheckoutTextInput
              label="City"
              name="billingCity"
              value={data.billingCity || ""}
              onChange={value => setData({ ...data, billingCity: value })}
              required
              showValidation={showValidation}
              flex={!isMobile}
              className="md:w-unset w-full"
            />
            {billingIsUS || billingIsCA ? (
              <CheckoutSelect
                label={billingIsCA ? "Province" : "State"}
                name="billingState"
                value={data.billingState}
                onChange={value => setData({ ...data, billingState: value })}
                data={billingIsCA ? CA_PROVINCES : US_STATES}
                required
                showValidation={showValidation}
                customValidation={
                  billingIsCA ? isValidCAProvince : isValidUSState
                }
              />
            ) : (
              <CheckoutTextInput
                label="State / Region (Optional)"
                name="billingState"
                value={data.billingState || ""}
                onChange={value => setData({ ...data, billingState: value })}
              />
            )}
            <CheckoutTextInput
              label={billingIsUS ? "ZIP Code" : "Postal Code"}
              name="billingZipcode"
              type={billingIsUS ? "number" : "text"}
              value={data.billingZipcode || ""}
              onChange={value => setData({ ...data, billingZipcode: value })}
              required
              showValidation={showValidation}
              customValidation={value =>
                billingIsUS
                  ? isValidZipForState(value, data.billingState)
                  : billingIsCA
                    ? isValidPostalForProvince(value, data.billingState)
                    : true
              }
            />
          </div>
        </div>
      </div>
      <div className="sticky bottom-0 z-20 grid grid-cols-2 gap-3 rounded-b-xl border-t border-[#00000014] bg-white px-4 py-3 md:flex md:justify-end">
        <Tooltip
          label={validationError}
          opened={showTooltip}
          position="top-end"
          withArrow
          arrowPosition="center"
          color="dark"
          multiline
          transitionProps={{ transition: "pop", duration: 300 }}
          styles={{
            tooltip: {
              minWidth: "200px",
              textAlign: "center",
            },
          }}
        >
          <Button
            type="submit"
            size="md"
            radius="xl"
            loading={
              nextIsPending ||
              updatePhoneNumber.isPending ||
              updateEmail.isPending ||
              updateShippingAddressForTax.isPending ||
              updateBillingAddress.isPending
            }
            w={isMobile ? "100%" : "200px"}
            h="40px"
            style={{ gridColumn: 2 }}
          >
            Save & Continue
          </Button>
        </Tooltip>
      </div>
    </form>
  );
};
